import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { reportsService } from '../services/reports';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TrendingUp, People, Phone, Assignment } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const Performance = () => {
  const { user } = useSelector((state) => state.auth);
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set to end of today for validation

  useEffect(() => {
    // Reset custom dates when period changes away from custom
    if (period !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
    fetchPerformanceData();
  }, [period]);

  useEffect(() => {
    // Fetch data when custom dates change
    if (period === 'custom' && startDate && endDate) {
      fetchPerformanceData();
    }
  }, [startDate, endDate]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { period };
      
      // Add date parameters based on period
      if (period === 'daily' || period === 'weekly') {
        const today = new Date();
        const todayStr = today.getFullYear() + '-' + 
          String(today.getMonth() + 1).padStart(2, '0') + '-' + 
          String(today.getDate()).padStart(2, '0');
        params.start_date = todayStr;
      } else if (period === 'custom') {
        if (!startDate || !endDate) {
          setLoading(false);
          return; // Don't fetch if custom dates are not set
        }
        const startStr = startDate.getFullYear() + '-' + 
          String(startDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(startDate.getDate()).padStart(2, '0');
        const endStr = endDate.getFullYear() + '-' + 
          String(endDate.getMonth() + 1).padStart(2, '0') + '-' + 
          String(endDate.getDate()).padStart(2, '0');
        params.start_date = startStr;
        params.end_date = endStr;
      }
      
      const response = await reportsService.getTeamPerformance(params);
      setPerformanceData(response);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      setError(error.response?.data?.message || 'Failed to fetch performance data');
      setPerformanceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleStartDateChange = (date) => {
    if (date && date <= today) {
      setStartDate(date);
      // If end date is before start date, clear it
      if (endDate && date > endDate) {
        setEndDate(null);
      }
    }
  };

  const handleEndDateChange = (date) => {
    if (date && date <= today && (!startDate || date >= startDate)) {
      setEndDate(date);
    }
  };

  if (loading && !performanceData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading performance data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">
            Team Performance
          </Typography>
          {performanceData?.date_range && performanceData.date_range.start_date && performanceData.date_range.end_date ? (
            <Typography variant="body2" color="text.secondary">
              {new Date(performanceData.date_range.start_date).toLocaleDateString()} - {new Date(performanceData.date_range.end_date).toLocaleDateString()}
            </Typography>
          ) : performanceData && (
            <Typography variant="body2" color="text.secondary">
              {period.charAt(0).toUpperCase() + period.slice(1)} Report
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              label="Period"
              disabled={loading}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {period === 'custom' && (
            <>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={today}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    sx: { minWidth: 150 }
                  } 
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                maxDate={today}
                disabled={!startDate}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    sx: { minWidth: 150 }
                  } 
                }}
              />
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {period === 'custom' && (!startDate || !endDate) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select both start and end dates to view custom performance data. End date cannot be later than today.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <Assignment />
                </Avatar>
                <Box>
                  <Typography variant="h4">
                    {performanceData?.overall_stats?.total_leads || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Leads
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <Phone />
                </Avatar>
                <Box>
                  <Typography variant="h4">
                    {performanceData?.overall_stats?.total_calls || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Calls
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="h4">
                    {performanceData?.overall_stats?.avg_conversion_rate || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Conversion
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <People />
                </Avatar>
                <Box>
                  <Typography variant="h4">
                    {performanceData?.overall_stats?.active_team_members || 0}/{performanceData?.overall_stats?.total_team_members || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active/Total Team
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Lead Status Distribution */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Lead Status Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                {performanceData?.status_distribution?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData.status_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="status" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#1976d2" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    flexDirection: 'column',
                    color: 'text.secondary'
                  }}>
                    <TrendingUp sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" color="text.secondary">
                      No status data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lead status distribution will appear here when data is available
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Team Performance Table */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Team Leaderboard
              </Typography>
              {performanceData?.team_performance?.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Leads</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Conv%</TableCell>
                        <TableCell align="right">Converted</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {performanceData.team_performance
                        .sort((a, b) => {
                          // Sort by conversion rate first, then by converted leads
                          if (b.conversion_rate !== a.conversion_rate) {
                            return b.conversion_rate - a.conversion_rate;
                          }
                          return (b.converted_leads || 0) - (a.converted_leads || 0);
                        })
                        .map((member, index) => (
                        <TableRow key={member.id || index}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                {member.name?.charAt(0) || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2">
                                  {member.name}
                                </Typography>
                                <Chip 
                                  label={member.role?.toUpperCase() || 'UNKNOWN'} 
                                  size="small" 
                                  variant="outlined"
                                  color={member.role === 'team_lead' ? 'primary' : 'default'}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{member.leads_assigned || 0}</TableCell>
                          <TableCell align="right">{member.calls_made || 0}</TableCell>
                          <TableCell align="right">{member.conversion_rate || 0}%</TableCell>
                          <TableCell align="right">{member.converted_leads || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <People sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    No team performance data available
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Team leaderboard will appear when data is available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Individual Performance Charts */}
        {performanceData?.team_performance?.length > 0 && (
          <>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Leads Assigned by Team Member
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={performanceData.team_performance}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="leads_assigned" fill="#2196F3" name="Leads Assigned" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Conversion Performance
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={performanceData.team_performance}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="converted_leads" fill="#4CAF50" name="Converted Leads" />
                        <Bar dataKey="calls_made" fill="#FF9800" name="Calls Made" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Activity Summary */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Team Activity Summary
                  </Typography>
                  <Grid container spacing={2}>
                    {performanceData.team_performance.map((member, index) => (
                      <Grid item xs={12} sm={6} md={4} key={member.id || index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Avatar sx={{ width: 40, height: 40 }}>
                                {member.name?.charAt(0) || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1">
                                  {member.name}
                                </Typography>
                                <Chip 
                                  label={member.role?.toUpperCase() || 'UNKNOWN'} 
                                  size="small" 
                                  color={member.role === 'team_lead' ? 'primary' : 'default'}
                                />
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Leads Assigned
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {member.leads_assigned || 0}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Calls Made
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {member.calls_made || 0}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Converted
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                {member.converted_leads || 0}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Conversion Rate
                              </Typography>
                              <Typography 
                                variant="body2" 
                                fontWeight="bold"
                                color={member.conversion_rate > 0 ? 'success.main' : 'text.secondary'}
                              >
                                {member.conversion_rate || 0}%
                              </Typography>
                            </Box>

                            {member.last_activity && (
                              <Typography variant="caption" color="text.secondary">
                                Last Activity: {new Date(member.last_activity).toLocaleDateString()}
                              </Typography>
                            )}

                            {!member.last_activity && member.leads_assigned === 0 && (
                              <Typography variant="caption" color="warning.main">
                                No activity recorded
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default Performance;